import { Component, Input, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { ChartConfiguration } from 'chart.js';
import { EcommerceService } from 'src/app/services/ecommerce.service';
import { ToastrService } from 'ngx-toastr';
import { environment } from 'src/environments/environment';

interface TerminalTrendPoint {
  date: string;
  commsFailureRate: number;
  totalAttempts?: number;
}

interface TerminalHealthRow {
  terminalId: string;
  terminalName?: string;
  commsFailureRate: number;
  errorRate: number;
  totalAttempts?: number;
  commsFailures?: number;
  approved?: number;
  declined?: number;
  errors?: number;
  chargedNoOrder?: number;
  trend: TerminalTrendPoint[];
  isChronic: boolean;
  chronicAssessmentSufficient: boolean;
  isNewTerminal: boolean;
  firstSeenAt?: string;
  terminalAgeDays?: number;
  deltaFromPriorPeriod: number | null;
  deltaPriorComparisonSufficient: boolean;
  deltaPriorComparisonDays?: number;
  deltaPriorComparisonAttempts?: number;
  deltaPriorComparisonRequestedDays?: number;
  sparklineData?: ChartConfiguration['data'];
  sparklineOptions?: ChartConfiguration['options'];
  isWorst?: boolean;
}

@Component({
  selector: 'app-terminal-health',
  templateUrl: './terminal-health.component.html',
  styleUrls: ['./terminal-health.component.scss']
})
export class TerminalHealthComponent implements OnInit, OnChanges {

  @Input() storeId: string = '';

  /**
   * Deliberate 30-day default for a monthly ops overview. The backend's parameterless
   * default is 7 days; we always send dateFrom/dateTo so both sides are explicit.
   */
  private readonly defaultWindowDays = 30;

  /** Meaningful rise vs prior window (rate points, 0.05 = 5 pp). */
  private readonly deltaRegressionThreshold = 0.05;
  /** Meaningful improvement vs prior window (rate points). */
  private readonly deltaImprovingThreshold = -0.05;

  /** From environment — max rate for Stable / green Improving signal badges. */
  private readonly reassuringRateFloor = environment.terminalHealth.reassuringRateFloor;
  private readonly rateColorElevated = environment.terminalHealth.rateColorElevated;
  private readonly rateColorWarn = environment.terminalHealth.rateColorWarn;
  private readonly rateColorBad = environment.terminalHealth.rateColorBad;
  private readonly rateColorCritical = environment.terminalHealth.rateColorCritical;

  /** Exposed for intro copy in template. */
  readonly reassuringRateFloorLabel = `${(environment.terminalHealth.reassuringRateFloor * 100).toFixed(0)}%`;

  terminals: TerminalHealthRow[] = [];
  isLoading = false;
  errorMessage = '';
  endpointUnavailable = false;

  dateFrom = '';
  dateTo = '';

  Math = Math;
  private loadSeq = 0;

  constructor(
    private ecommerceService: EcommerceService,
    private toastr: ToastrService
  ) {}

  ngOnInit(): void {
    this.setDefaultDateRange();
    if (this.storeId) {
      this.loadHealth();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['storeId'] && !changes['storeId'].firstChange) {
      this.resetForStoreChange();
      if (this.storeId) {
        this.loadHealth();
      }
    }
  }

  private resetForStoreChange(): void {
    this.loadSeq++;
    this.terminals = [];
    this.errorMessage = '';
    this.endpointUnavailable = false;
  }

  private setDefaultDateRange(): void {
    const to = new Date();
    const from = new Date();
    from.setDate(from.getDate() - this.defaultWindowDays);
    this.dateTo = to.toISOString().slice(0, 10);
    this.dateFrom = from.toISOString().slice(0, 10);
  }

  private buildQueryParams(): any {
    const params: any = {};
    if (this.dateFrom) params.dateFrom = this.dateFrom;
    if (this.dateTo) params.dateTo = this.dateTo;
    return params;
  }

  loadHealth(): void {
    const seq = ++this.loadSeq;
    const requestedStoreId = this.storeId;

    this.isLoading = true;
    this.errorMessage = '';
    this.endpointUnavailable = false;

    this.ecommerceService.getTerminalHealth(requestedStoreId, this.buildQueryParams()).subscribe({
      next: (res: any) => {
        if (seq !== this.loadSeq || requestedStoreId !== this.storeId) {
          return;
        }
        const data = res?.response?.data ?? res?.data ?? {};
        const raw = data.terminals || [];
        this.terminals = this.normalizeTerminals(Array.isArray(raw) ? raw : []);
        this.isLoading = false;
      },
      error: (error: any) => {
        if (seq !== this.loadSeq || requestedStoreId !== this.storeId) {
          return;
        }
        this.isLoading = false;
        if (error?.status === 404) {
          this.endpointUnavailable = true;
          this.errorMessage = 'Terminal health API is not deployed yet.';
          this.terminals = [];
          return;
        }
        this.errorMessage = error?.error?.response?.message || 'Failed to load terminal health';
        this.toastr.error(this.errorMessage);
      }
    });
  }

  private normalizeTerminals(raw: any[]): TerminalHealthRow[] {
    const rows: TerminalHealthRow[] = raw.map(t => ({
      terminalId: t.terminalId || 'unknown',
      terminalName: t.terminalName,
      commsFailureRate: Number(t.commsFailureRate) || 0,
      errorRate: Number(t.errorRate) || 0,
      totalAttempts: t.totalAttempts,
      commsFailures: t.commsFailures,
      approved: t.approved,
      declined: t.declined,
      errors: t.errors,
      chargedNoOrder: t.chargedNoOrder,
      trend: this.normalizeTrend(t.trend),
      isChronic: t.isChronic === true,
      chronicAssessmentSufficient: t.chronicAssessmentSufficient === true,
      isNewTerminal: t.isNewTerminal === true,
      firstSeenAt: t.firstSeenAt,
      terminalAgeDays: t.terminalAgeDays,
      deltaFromPriorPeriod: t.deltaFromPriorPeriod ?? null,
      deltaPriorComparisonSufficient: t.deltaPriorComparisonSufficient === true,
      deltaPriorComparisonDays: t.deltaPriorComparisonDays,
      deltaPriorComparisonAttempts: t.deltaPriorComparisonAttempts,
      deltaPriorComparisonRequestedDays: t.deltaPriorComparisonRequestedDays
    })).sort((a, b) => this.compareTerminalPriority(a, b));

    const actionable = rows.find(r => this.isNewRegression(r))
      || rows.find(r => this.isNewTerminalHighRate(r))
      || rows.find(r => this.isElevatedAbsolute(r) && r.commsFailureRate >= 0.25);
    if (actionable) {
      actionable.isWorst = true;
    }

    rows.forEach(row => {
      row.sparklineData = this.buildSparklineData(row.trend);
      row.sparklineOptions = this.buildSparklineOptions();
    });

    return rows;
  }

  private normalizeTrend(trend: any[]): TerminalTrendPoint[] {
    if (!Array.isArray(trend)) return [];
    return trend
      .map(point => ({
        date: (point.date || '').slice(0, 10),
        commsFailureRate: Number(point.commsFailureRate) || 0,
        totalAttempts: point.totalAttempts
      }))
      .filter(p => p.date);
  }

  /**
   * Build sparkline with explicit gaps for calendar days omitted from trend
   * (days with zero attempts are not returned by the backend).
   */
  private buildSparklineData(trend: TerminalTrendPoint[]): ChartConfiguration['data'] {
    if (!trend.length || !this.dateFrom || !this.dateTo) {
      return { labels: [], datasets: [{ data: [], borderColor: '#dc3545', borderWidth: 2 }] };
    }

    const rateByDay = new Map<string, number>();
    trend.forEach(p => rateByDay.set(p.date, p.commsFailureRate));

    const labels: string[] = [];
    const data: (number | null)[] = [];

    const cursor = new Date(this.dateFrom + 'T00:00:00');
    const end = new Date(this.dateTo + 'T00:00:00');

    while (cursor <= end) {
      const key = cursor.toISOString().slice(0, 10);
      labels.push(key.slice(5));
      if (rateByDay.has(key)) {
        data.push(Math.round(rateByDay.get(key)! * 1000) / 10);
      } else {
        data.push(null);
      }
      cursor.setDate(cursor.getDate() + 1);
    }

    return {
      labels,
      datasets: [{
        data,
        borderColor: '#dc3545',
        backgroundColor: 'rgba(220, 53, 69, 0.12)',
        borderWidth: 2,
        fill: true,
        tension: 0,
        pointRadius: 0,
        spanGaps: false
      }]
    };
  }

  private buildSparklineOptions(): ChartConfiguration['options'] {
    return {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: { display: false },
        tooltip: {
          enabled: true,
          callbacks: {
            label: (ctx) => {
              const v = ctx.parsed.y;
              return v == null ? 'No attempts' : `${v.toFixed(1)}% comms failure`;
            }
          }
        }
      },
      scales: {
        x: { display: false },
        y: {
          display: false,
          min: 0,
          max: 100
        }
      }
    };
  }

  onFilterChange(): void {
    this.loadHealth();
  }

  resetFilters(): void {
    this.setDefaultDateRange();
    this.loadHealth();
  }

  /** Format 0-1 fraction as percentage for display. */
  formatRate(rate: number): string {
    return `${(rate * 100).toFixed(1)}%`;
  }

  /** deltaFromPriorPeriod is in rate points (0.12 = 12 pp). */
  formatDelta(delta: number): string {
    const pp = delta * 100;
    const sign = pp > 0 ? '+' : '';
    return `${sign}${pp.toFixed(1)} pp`;
  }

  hasDeltaComparison(row: TerminalHealthRow): boolean {
    return row.deltaPriorComparisonSufficient === true;
  }

  hasInsufficientHistory(row: TerminalHealthRow): boolean {
    return !this.hasDeltaComparison(row);
  }

  /**
   * Recently worsened vs prior window — highest-precedence signal.
   * A measured regression always wins over absence-of-data badges.
   */
  isNewRegression(row: TerminalHealthRow): boolean {
    return this.hasDeltaComparison(row)
      && row.deltaFromPriorPeriod !== null
      && row.deltaFromPriorPeriod >= this.deltaRegressionThreshold;
  }

  isHighAbsoluteRate(row: TerminalHealthRow): boolean {
    return row.commsFailureRate >= this.reassuringRateFloor;
  }

  /** Delta trend only — does not imply the signal badge is reassuring. */
  private isImprovingTrend(row: TerminalHealthRow): boolean {
    return this.hasDeltaComparison(row)
      && row.deltaFromPriorPeriod !== null
      && row.deltaFromPriorPeriod <= this.deltaImprovingThreshold
      && !this.isNewRegression(row);
  }

  /** Green Improving badge — only when the absolute rate is already acceptable. */
  isImproving(row: TerminalHealthRow): boolean {
    return this.isImprovingTrend(row) && !this.isHighAbsoluteRate(row);
  }

  /** Trending down but still failing too often to reassure. */
  isImprovingStillHigh(row: TerminalHealthRow): boolean {
    return this.isImprovingTrend(row) && this.isHighAbsoluteRate(row);
  }

  isStable(row: TerminalHealthRow): boolean {
    return this.hasDeltaComparison(row)
      && row.deltaFromPriorPeriod !== null
      && !this.shouldShowChronicBadge(row)
      && !this.isNewRegression(row)
      && !this.isImprovingTrend(row)
      && !this.isHighAbsoluteRate(row);
  }

  /**
   * High absolute failure rate without a higher-priority trend badge.
   * Covers "persistently terrible with a flat delta" — stable fever, not healthy stable.
   */
  isElevatedAbsolute(row: TerminalHealthRow): boolean {
    return this.isHighAbsoluteRate(row)
      && !this.isNewRegression(row)
      && !this.isNewTerminalHighRate(row)
      && !this.canShowChronic(row)
      && !this.isImprovingStillHigh(row);
  }

  /** Chronic eligibility from backend — does not imply the badge is shown. */
  canShowChronic(row: TerminalHealthRow): boolean {
    return row.chronicAssessmentSufficient && row.isChronic;
  }

  /**
   * Chronic badge — suppressed when a sufficient negative delta shows recovery.
   * "Bad for ages but measurably getting better" is news; muted chronic would hide it.
   */
  shouldShowChronicBadge(row: TerminalHealthRow): boolean {
    return this.canShowChronic(row)
      && !this.isNewRegression(row)
      && !this.isNewTerminalHighRate(row)
      && !this.isImprovingTrend(row);
  }

  /**
   * Genuinely new terminal (no data before dateFrom) with high absolute failure rate.
   * Never shown when a valid regression delta exists — precedence is explicit.
   */
  isNewTerminalHighRate(row: TerminalHealthRow): boolean {
    return row.isNewTerminal
      && !this.isNewRegression(row)
      && row.commsFailureRate >= this.reassuringRateFloor;
  }

  /** Prior-window delta unavailable — not the same as "terminal is new". */
  isQuietInsufficientHistory(row: TerminalHealthRow): boolean {
    return this.hasInsufficientHistory(row)
      && !this.isNewRegression(row)
      && !this.isNewTerminalHighRate(row)
      && !this.canShowChronic(row)
      && !this.isElevatedAbsolute(row)
      && !this.isHighAbsoluteRate(row);
  }

  getImprovingStillHighLabel(row: TerminalHealthRow): string {
    return `Improving — still ${this.formatRate(row.commsFailureRate)}`;
  }

  getElevatedAbsoluteLabel(row: TerminalHealthRow): string {
    return `Elevated — ${this.formatRate(row.commsFailureRate)}`;
  }

  getElevatedAbsoluteTooltip(row: TerminalHealthRow): string {
    const deltaPart = row.deltaFromPriorPeriod != null
      ? ` Delta vs prior: ${this.formatDelta(row.deltaFromPriorPeriod)}.`
      : '';
    return `Comms failure rate is currently high.${deltaPart} A flat or small delta does not mean healthy — investigate this unit.`;
  }

  getImprovingStillHighTooltip(row: TerminalHealthRow): string {
    return `Failure rate dropped vs the prior window but is still ${this.formatRate(row.commsFailureRate)} — not yet acceptable.`;
  }

  getInsufficientHistoryTooltip(row: TerminalHealthRow): string {
    const days = row.deltaPriorComparisonDays ?? 0;
    const attempts = row.deltaPriorComparisonAttempts ?? 0;
    const requested = row.deltaPriorComparisonRequestedDays;
    const requestedPart = requested != null ? ` of ${requested} requested days` : '';
    return `Only ${days} day(s) with data${requestedPart} and ${attempts} attempt(s) in the prior window — delta comparison not possible yet.`;
  }

  getNewTerminalHighRateTooltip(row: TerminalHealthRow): string {
    const agePart = row.terminalAgeDays != null
      ? `First seen ${row.terminalAgeDays} day(s) ago`
      : row.firstSeenAt
        ? `First seen ${row.firstSeenAt.slice(0, 10)}`
        : 'No prior data before this window';
    return `${agePart} — absolute comms failure rate is already high. Too new for a meaningful delta comparison.`;
  }

  getChronicTooltip(row: TerminalHealthRow): string {
    if (!row.chronicAssessmentSufficient) {
      return 'Selected window is too short to assess whether this is a chronic issue.';
    }
    return 'Elevated comms failure rate across most of the selected range — known ongoing issue';
  }

  /**
   * Mutually exclusive primary signal for tests and sorting.
   * Precedence: regression > new-terminal-high > improving* > elevated > chronic > stable/quiet.
   */
  resolveSignalBadge(row: TerminalHealthRow): string {
    if (this.isNewRegression(row)) return 'new-regression';
    if (this.isNewTerminalHighRate(row)) return 'new-terminal-high';
    if (this.isImprovingStillHigh(row)) return 'improving-still-high';
    if (this.isImproving(row)) return 'improving';
    if (this.shouldShowChronicBadge(row)) return 'chronic';
    if (this.isElevatedAbsolute(row)) return 'elevated';
    if (this.isQuietInsufficientHistory(row)) return 'insufficient-history';
    if (this.isStable(row)) return 'stable';
    return 'none';
  }

  private signalPriority(row: TerminalHealthRow): number {
    if (this.isNewRegression(row)) return 6;
    if (this.isNewTerminalHighRate(row)) return 5;
    if (this.isImprovingStillHigh(row) || this.isImproving(row)) return 4;
    if (this.isElevatedAbsolute(row)) return 4;
    if (this.shouldShowChronicBadge(row)) return 3;
    return 2;
  }

  private compareTerminalPriority(a: TerminalHealthRow, b: TerminalHealthRow): number {
    const priorityDiff = this.signalPriority(b) - this.signalPriority(a);
    if (priorityDiff !== 0) {
      return priorityDiff;
    }
    return b.commsFailureRate - a.commsFailureRate;
  }

  getRateBarClass(rate: number): string {
    if (rate >= this.rateColorCritical) return 'rate-critical';
    if (rate >= this.rateColorBad) return 'rate-bad';
    if (rate >= this.rateColorWarn) return 'rate-warn';
    if (rate >= this.rateColorElevated) return 'rate-elevated';
    return 'rate-ok';
  }

  getRateBadgeClass(rate: number): string {
    if (rate >= this.rateColorBad) return 'bg-danger';
    if (rate >= this.rateColorElevated) return 'bg-warning text-dark';
    return 'bg-success';
  }

  trackByTerminalId(index: number, row: TerminalHealthRow): string {
    return row.terminalId || String(index);
  }
}
